import { createColumnHelper } from '@tanstack/react-table';
import { ConnectedEventInfo } from 'src/components/Events/ConnectedEventInfo';
import { EventDateDisplay } from 'src/components/Events/EventInfo';
import { BaseCell, IconTextCell, TextCell } from 'src/components/TableV2/cells';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_BASE_L,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { FormatOption } from 'src/modals/EditTableColumns';
import { RecentSalesTableColumnPicker } from 'src/navigations/Routes/Home/RecentSalesSection/RecentSalesTableColumnPicker';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { createDateColumn } from 'src/tables/TableV2/factories/dateColumnFactory';
import { createLinkColumn } from 'src/tables/TableV2/factories/linkColumnFactory';
import { createMoneyColumn } from 'src/tables/TableV2/factories/moneyColumnFactory';
import { createPercentColumn } from 'src/tables/TableV2/factories/percentColumnFactory';
import { createSeatingColumn } from 'src/tables/TableV2/factories/seatingColumnFactory';
import { createUserTagColumn } from 'src/tables/TableV2/factories/userTagColumnFactory';
import { useContextBasedUsers } from 'src/tables/TableV2/factories/userTagColumnHooks';
import { RecentSalesTableColumnId } from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID } from 'src/utils/constants/contentIdMaps';
import {
  compareEventsByDate,
  compareEventsByName,
  compareEventsByVenue,
} from 'src/utils/eventWithDataUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import {
  getInHandDisplayData,
  getSaleDetailsRelativeUrl,
  getSaleStatusDisplayContentId,
  getSaleStatusIcon,
} from 'src/utils/saleUtils';
import { safeSortDateTime } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  DateTimeRange,
  MarketplaceFulfillmentState,
  PointOfSaleSaleStatus,
} from 'src/WebApiController';

import * as styles from '../../../navigations/Routes/Home/RecentSalesSection/RecentSalesSection.css';
import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
import { MarketplaceInfoCell } from './MarketplaceInfoCell';
import { SalesActionsCell } from './SalesActionsCell';
import { SoldQuantityTicketTypeCell } from './SoldQuantityTicketTypeCell';
const columnHelper = createColumnHelper<SaleWithEvent | null>();

export const defaultRecentSalesV3Columns = [
  RecentSalesTableColumnId.EventName,
  RecentSalesTableColumnId.EventDate,
  RecentSalesTableColumnId.OrderDate,
  RecentSalesTableColumnId.Venue,
  RecentSalesTableColumnId.Seating,
  RecentSalesTableColumnId.Marketplace,
  RecentSalesTableColumnId.SoldQuantityTicketType,
  RecentSalesTableColumnId.SaleId,
  RecentSalesTableColumnId.PurchasedBy,
  RecentSalesTableColumnId.Status,
  RecentSalesTableColumnId.InHandDate,
  RecentSalesTableColumnId.Proceeds,
  RecentSalesTableColumnId.PandL,
  RecentSalesTableColumnId.Margin,
  RecentSalesTableColumnId.Actions,
];

export const RECENT_SALES_TABLE_COLUMNS_CONFIG_V3 = {
  // Same as SalesTableFlattenedColumnId.Event
  // Just hardcoded the precision to be event name only
  // When Recent Sales supports column picker, we can merge it back
  [RecentSalesTableColumnId.EventName]: columnHelper.accessor((data) => data, {
    id: RecentSalesTableColumnId.EventName,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
              id as RecentSalesTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, table }) {
      const sale = getValue<SaleWithEvent>();

      if (!sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!sale.event) {
        return null;
      }

      return (
        <BaseCell>
          <ConnectedEventInfo
            showYear={true}
            showEventDateTbd
            event={sale.event}
            performer={sale.performer}
            venue={sale.venue}
            eventNameOnly={true}
            slimMode
            useShortName
            showEventStatus
          />
        </BaseCell>
      );
    },
    sortingFn: compareEventsByName,
  }),
  [RecentSalesTableColumnId.EventDate]: columnHelper.accessor(
    (data) => data?.event?.dates,
    {
      id: RecentSalesTableColumnId.EventDate,
      header: () => (
        <TableCellDiv align="left">
          <Content id={ContentId.EventDate} />
        </TableCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original }, column, table }) {
        const columnId = column.id;

        const value = getValue<DateTimeRange | undefined>();
        const {
          userDefinedPrecision = FormatOption.DateTime_MonthDD_Time_Day,
        } = useColumnUserSetting(columnId, SectionType.RecentSalesTable);

        if (!original || !original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const customPrecision =
          table.options.meta?.formatOptionOverrides?.[
            RecentSalesTableColumnId.EventDate
          ];

        const date = applyNumberFormatting({
          precision: customPrecision ?? userDefinedPrecision,
          inputNumber: value?.start,
          isDateTime: true,
        }) as string;

        return (
          <BaseCell>
            {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
              <EventDateDisplay
                eventDateString={value?.start ?? undefined}
                eventEndDateString={value?.end ?? undefined}
                showEventDateTbd={true}
                eventSubDateFormat={' - h:mm aaa, eee'}
                slimMode
                displayClassName={styles.rowDataText}
              />
            ) : (
              <div
                style={{
                  textTransform: 'none',
                }}
                className={styles.rowDataText}
              >
                {date}
              </div>
            )}
          </BaseCell>
        );
      },
      sortingFn: compareEventsByDate,
    }
  ),
  [RecentSalesTableColumnId.Seating]: createSeatingColumn<SaleWithEvent | null>(
    {
      id: RecentSalesTableColumnId.Seating,
      headerContentId:
        RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
          RecentSalesTableColumnId.Seating
        ],
      accessor: (data) =>
        data
          ? {
              seating: data.sale.seating,
              listingNotes: data.sale.seatTraits,
              isSeatSaver: data.sale.isSeatSaver,
              status: data.sale.status,
            }
          : null,
      hasMarketplaceProblemAccessor: (data) =>
        data?.sale?.hasMarketplaceProblem ?? false,
      size: COLUMN_DEFAULT_SIZE_LG,
      sectionType: SectionType.RecentSalesTable,
      sortingFn: SEATING_SORTING_FN_NAME,
    }
  ),
  [RecentSalesTableColumnId.Venue]: columnHelper.accessor((data) => data, {
    id: RecentSalesTableColumnId.Venue,
    header: ({ column: { id } }) => (
      <Content
        id={
          RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
            id as RecentSalesTableColumnId
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, table }) {
      const data = getValue<SaleWithEvent>();

      if (!data || !data.venue) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TextCell
          value={data.venue.shortName || data.venue.name}
          className={styles.venueCell}
        />
      );
    },
    sortingFn: compareEventsByVenue,
  }),
  [RecentSalesTableColumnId.Status]: columnHelper.accessor(
    (data) => {
      return data ? data.sale.status : null;
    },
    {
      id: RecentSalesTableColumnId.Status,
      header: ({ column: { id } }) => (
        <Content
          id={
            RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
              id as RecentSalesTableColumnId
            ]
          }
        />
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, column: { id: columnId }, row, table }) {
        const { userDefinedPrecision } = useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SectionType.RecentSalesTable
        );

        const sale = row.original?.sale;
        const value = getValue() as PointOfSaleSaleStatus;

        const statusContent = useContent(
          getSaleStatusDisplayContentId(value, sale?.mkpContrState, sale?.mkp)
        );

        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const SaleStatusIcon = getSaleStatusIcon(
          value,
          sale?.mkpContrState,
          sale?.mkp
        );

        if (!statusContent) {
          return null;
        }

        return (
          <BaseCell showPadding={false}>
            <Stack
              gap="m"
              alignItems="center"
              style={{
                backgroundColor: vars.color.surfaceSecondary,
                padding: vars.spacing.xs,
                borderRadius: vars.borderRadius.s,
                width: '100%',
                height: '100%',
              }}
            >
              {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
                <SaleStatusIcon fill={'transparent'} title={statusContent} />
              ) : (
                <div style={{ overflow: 'hidden' }}>{statusContent}</div>
              )}

              {
                // if the Sale is fulfilled in both POS and Marketplace, but the version is mismatched, show the warning
                value === PointOfSaleSaleStatus.Fulfilled &&
                sale?.isFulfillVerMis ? (
                  <Stack>
                    <TooltipPopover
                      variant="link"
                      triggerContent={
                        <WarningOutlineIcon
                          fill={IconsFill.textWarning}
                          withHoverEffect
                        />
                      }
                    >
                      <WarningMessage
                        message={
                          <Content id={ContentId.VersionMismatchExplanation} />
                        }
                      />
                    </TooltipPopover>
                  </Stack>
                ) : value !== PointOfSaleSaleStatus.Fulfilled &&
                  sale?.mkpFulfState ===
                    MarketplaceFulfillmentState.Fulfilled ? (
                  <Stack>
                    <TooltipPopover
                      variant="link"
                      triggerContent={
                        <WarningOutlineIcon
                          fill={IconsFill.textWarning}
                          withHoverEffect
                        />
                      }
                    >
                      <WarningMessage
                        message={
                          <Content
                            id={ContentId.SaleIsFulfilledOnMarketplace}
                          />
                        }
                      />
                    </TooltipPopover>
                  </Stack>
                ) : undefined
              }
            </Stack>
          </BaseCell>
        );
      },
    }
  ),
  [RecentSalesTableColumnId.Marketplace]: columnHelper.accessor(
    (data) => data?.sale?.mkp,
    {
      id: RecentSalesTableColumnId.Marketplace,
      header: ({ column: { id } }) => (
        <Content
          id={
            RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
              id as RecentSalesTableColumnId
            ]
          }
        />
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ column: { id: columnId }, row: { original } }) {
        if (original == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        return (
          <BaseCell>
            <MarketplaceInfoCell sale={original} useV2Style />
          </BaseCell>
        );
      },
    }
  ),
  [RecentSalesTableColumnId.SoldQuantityTicketType]: columnHelper.accessor(
    (data) => data?.sale?.qtySold,
    {
      id: RecentSalesTableColumnId.SoldQuantityTicketType,
      header: ({ column: { id } }) => null,
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        column: { id: columnId },
        row: { original },
      }) {
        if (original == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        return (
          <BaseCell>
            <SoldQuantityTicketTypeCell sale={original} useV2Style />
          </BaseCell>
        );
      },
    }
  ),
  [RecentSalesTableColumnId.SaleId]: createLinkColumn<SaleWithEvent | null>({
    id: RecentSalesTableColumnId.SaleId,
    headerContentId:
      RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
        RecentSalesTableColumnId.SaleId
      ],
    linkAccessor: (data) =>
      data?.sale?.id ? getSaleDetailsRelativeUrl(data.sale.id) : null,
    textAccessor: (data) =>
      data?.sale?.id ? String(data.sale.id) : data?.sale?.idOnMkp,
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    sectionType: SectionType.RecentSalesTable,
  }),
  [RecentSalesTableColumnId.Proceeds]: createMoneyColumn<SaleWithEvent | null>({
    id: RecentSalesTableColumnId.Proceeds,
    headerContentId:
      RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
        RecentSalesTableColumnId.Proceeds
      ],
    accessor: (data) => data?.sale?.ttlNetProcs,
  }),
  [RecentSalesTableColumnId.PandL]: createMoneyColumn<SaleWithEvent | null>({
    id: RecentSalesTableColumnId.PandL,
    headerContentId:
      RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
        RecentSalesTableColumnId.PandL
      ],
    accessor: (data) => data?.sale?.pandL,
    withThresholds: true,
  }),
  [RecentSalesTableColumnId.Margin]: createPercentColumn<SaleWithEvent | null>({
    id: RecentSalesTableColumnId.Margin,
    headerContentId:
      RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
        RecentSalesTableColumnId.Margin
      ],
    accessor: (data) => data?.sale?.margin,
    withThresholds: true,
  }),
  [RecentSalesTableColumnId.PurchasedBy]: createUserTagColumn<SaleWithEvent | null>(
    {
      id: RecentSalesTableColumnId.PurchasedBy,
      headerContentId:
        RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
          RecentSalesTableColumnId.PurchasedBy
        ],
      accessor: (data) => data?.sale?.owners?.buyerIds,
      useUserDisplayHook: useContextBasedUsers,
      size: COLUMN_DEFAULT_SIZE_LG,
      enableColumnFilter: false,
    }
  ),
  [RecentSalesTableColumnId.Actions]: columnHelper.display({
    id: RecentSalesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: false,
    header: function Cell() {
      return <RecentSalesTableColumnPicker variant="compact" />;
    },
    enableColumnFilter: false,
    cell: ({ row: { original }, table }) => {
      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          RecentSalesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      return !original ? (
        <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
      ) : (
        <BaseCell>
          <SalesActionsCell
            linkOnly={linkOnly}
            saleId={original.sale.id}
            marketplaceSaleId={original.sale.idOnMkp}
            marketplace={original.sale.mkp}
            saleStatus={original.sale.status}
            actions={original.sale.actions}
            ticketType={original.sale.ticketType}
            isNoFulfill={original.sale.isNoFulfill}
          />
        </BaseCell>
      );
    },
    meta: {
      align: 'right',
    },
  }),
  [RecentSalesTableColumnId.InHandDate]: columnHelper.accessor(
    (data) => data?.sale.inHandAt,
    {
      id: RecentSalesTableColumnId.InHandDate,
      header: () => <Content id={ContentId.InHandDate} />,
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const value = getValue<string | null>();
        const inhand = useContent(ContentId.InHand);
        const date = value ? new Date(value) : null;
        const dateLocal =
          date &&
          new Date(
            Date.UTC(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              date.getHours(),
              date.getMinutes(),
              date.getSeconds()
            )
          );
        const needsToFulfill = Boolean(
          original?.sale.status &&
            ![
              PointOfSaleSaleStatus.ProcessingFulfillment,
              PointOfSaleSaleStatus.Fulfilled,
              PointOfSaleSaleStatus.CancelledHold,
              PointOfSaleSaleStatus.Rejected,
              PointOfSaleSaleStatus.PendingRejection,
            ].includes(original?.sale.status)
        );

        const { displayText, displayTooltip, alertIcon } = getInHandDisplayData(
          inhand,
          dateLocal,
          new Date(),
          needsToFulfill,
          true
        );

        return <IconTextCell iconLeft={alertIcon} text={displayText || ''} />;
      },
      sortingFn: safeSortDateTime<SaleWithEvent>,
    }
  ),
  [RecentSalesTableColumnId.OrderDate]: createDateColumn<SaleWithEvent | null>({
    id: RecentSalesTableColumnId.OrderDate,
    headerContentId:
      RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
        RecentSalesTableColumnId.OrderDate
      ],
    accessor: (data) => data?.sale.saleDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
} as const;
